import {useAutomation} from '@github-ui/repo-agents/hooks/use-automation'
import type {BaseTask} from '../types/task'
import type {APITask} from '../types/api-task'
import {useSidebarAgentFilter} from '../contexts/SidebarAgentFilterContext'

export type TaskAuthor = {
  /** Resolved base display name: automation display name, creator login, or fallback */
  displayName: string
  /** Raw creator login, if available */
  login?: string
  /** Whether this task was created by an automation agent */
  isAutomation: boolean
  /** The automation's configured prompt, when this is an automation task */
  prompt?: string
  /** True while the automation display name is still being fetched */
  isLoading: boolean
}

type TaskAuthorInput = Pick<BaseTask, 'automation_id' | 'creator_login'> | APITask

function extractFields(task: TaskAuthorInput) {
  if ('creator' in task) {
    // APITask / HydratedAPITask shape
    return {
      automationId: task.automation_id ?? undefined,
      creatorLogin: task.creator?.login,
    }
  }
  // BaseTask shape
  return {
    automationId: task.automation_id ?? undefined,
    creatorLogin: task.creator_login,
  }
}

/**
 * Resolves the author of a task, handling automation display name fetching.
 *
 * Accepts either a BaseTask (list views) or APITask/HydratedAPITask (detail views).
 * Returns a base display name — callers can override with "You" or other
 * context-specific labels.
 */
export function useTaskAuthor(
  task: TaskAuthorInput | undefined,
  repoOwner?: string,
  repoName?: string,
  fallback = 'Unknown',
): TaskAuthor {
  const fields = task ? extractFields(task) : undefined
  const {automationsVisible} = useSidebarAgentFilter()
  const isAutomation = !!fields?.automationId && automationsVisible

  const {automation, isLoading} = useAutomation({
    owner: repoOwner ?? '',
    repo: repoName ?? '',
    automationId: isAutomation ? fields?.automationId : undefined,
  })

  let displayName: string
  if (isAutomation) {
    displayName = automation?.name ?? 'Automation'
  } else {
    displayName = fields?.creatorLogin ?? fallback
  }

  return {
    displayName,
    login: fields?.creatorLogin,
    isAutomation,
    prompt: isAutomation ? automation?.prompt : undefined,
    isLoading: isAutomation && isLoading,
  }
}
